import { routes } from './routes';

// Pure mapping between legacy evtChangeView payloads and routes. No window / document / iframe
// access here: everything is unit-tested in the node vitest environment.

// Transitional reset views: the legacy publishes them right before the real target
// (visitPreview.js, pos.js, AUTOLOAD boot), so they must never touch the URL.
export const IGNORED_LEGACY_VIEWS = new Set(['greeter']);

// Two legacy publishes closer than this are one logical navigation (c_resetView() then the
// target 50-150ms later, setTimeout(amplify.publish, 250|350, ...) call sites).
export const COALESCE_MS = 400;

const EMPTY_GUID = /^0{8}-0{4}-0{4}-0{4}-0{12}$/;
const PRINTABLE_ASCII = /^[\x20-\x7e]+$/;

export const legacyRoutes = routes.filter((route) => route.meta?.legacyView);

const routeByLegacyView = new Map();
legacyRoutes.forEach((route) => [route.meta.legacyView, ...(route.meta.aliases ?? [])].forEach((view) => routeByLegacyView.set(view, route)));

export const routeNameForLegacyView = (view) => routeByLegacyView.get(view)?.name ?? null;

const identity = (value) => ((typeof value === 'string' || typeof value === 'number') && String(value).length > 0 ? String(value) : null);

const toNumberIfNumeric = (value) => (/^\d+$/.test(value) ? Number(value) : value);

const hasParams = (params) => Object.values(params ?? {}).some((value) => (Array.isArray(value) ? value.length > 0 : value != null && value !== ''));

// `data` is not always an object: eagle.js publishes data: 'OFFLINE-ORDERS'.
function paramsForLegacyEvent(view, data) {
	const payload = data && typeof data === 'object' ? data : null;

	switch (view) {
		case 'vw-visit': {
			const uid = identity(payload?.appointment?.uid) ?? identity(payload?.appointmentUid);
			return uid && !EMPTY_GUID.test(uid) ? { uid } : {};
		}
		case 'vwOrdersTrackingAssort': {
			const orderId = identity(payload?.orderId);
			return orderId ? { orderId } : {};
		}
		case 'vwDocFolders': {
			const folderId = identity(payload?.folderId);
			return folderId ? { folderId } : {};
		}
		default:
			return {};
	}
}

export function routeLocationForLegacyEvent(evt) {
	const view = evt?.view;
	if (!view || IGNORED_LEGACY_VIEWS.has(view)) return null;

	const route = routeByLegacyView.get(view);
	if (!route) return null;

	return { name: route.name, params: paramsForLegacyEvent(view, evt.data) };
}

// Data the legacy needs to open a view it has no recorded payload for (reload, pasted URL, history
// entry older than the current iframe document). Shapes mirror existing legacy publishers.
export function coldDataForRoute(route, { appointmentType } = {}) {
	const view = route?.meta?.legacyView;
	const params = route?.params ?? {};

	if (view === 'vw-visit' && params.uid) return { readonly: true, appointment: { uid: params.uid, type: appointmentType } };
	if (view === 'vwOrdersTrackingAssort' && params.orderId) return { orderId: toNumberIfNumeric(params.orderId) };
	if (view === 'vwDocFolders' && params.folderId) return { folderId: toNumberIfNumeric(params.folderId) };
	return null;
}

// `remembered` is the last payload the legacy itself published for this URL: replaying it keeps
// the exact appMenu (vwDocObjectives lives under menuSFA and menuDOC, which flips ctx.app) and the
// live `data` objects a URL cannot carry.
export function legacyPayloadForRoute(route, remembered = null, options = {}) {
	const view = route?.meta?.legacyView;
	if (!view) return null;

	if (remembered && remembered.view === view) return { appMenu: remembered.appMenu, view, label: remembered.label, data: remembered.data ?? null };
	return { appMenu: route.meta.appMenu, view, data: coldDataForRoute(route, options) };
}

// Value for the legacy AUTOLOAD cookie ("<appMenu>|<view>|<data>", read with a bare atob() by
// cookieHelper), or null when the route cannot be replayed cold.
export function buildAutoloadValue(route) {
	const meta = route?.meta;
	if (!meta?.legacyView || !meta.coldStart || hasParams(route.params)) return null;

	const value = `${meta.appMenu}|${meta.legacyView}|`;
	return PRINTABLE_ASCII.test(value) ? value : null;
}

export function decideHistoryMode({ embedded = false, firstCommit = false, fromPortal = false, state = null, routeHistoryMode = null, sinceLastLegacyNav = Infinity, previousPath = null, targetPath = null } = {}) {
	// Inside the BO frameset, pushState would pollute the BO operator's own Back stack.
	if (embedded) return 'replace';
	if (firstCommit) return 'replace';
	// Leaving the portal is a legacy hasher navigation ('' → 'ecos'): the iframe already added the history entry.
	if (fromPortal) return 'replace';
	// Only ecosVM.backHandler() publishes `state`: the legacy just went back itself.
	if (state != null) return previousPath && previousPath === targetPath ? 'back' : 'replace';
	if (routeHistoryMode === 'replace') return 'replace';
	if (sinceLastLegacyNav < COALESCE_MS) return 'replace';
	return 'push';
}

export function readLegacySessionUserId(storage) {
	try {
		const id = JSON.parse(storage?.getItem('context') ?? 'null')?.user?.id;
		return id > 0 ? id : null;
	} catch {
		return null;
	}
}
